import { useForm } from 'react-hook-form';
import { Flex, Stack, Textarea } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatActivityType } from '@shinetools/ape-code-library';
import { z } from 'zod';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import zodLocales from 'common/zod/locales';
import * as Form from 'components/_core/form';
import Text from 'components/_core/Text';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';

import Header from '../../../../components/Header';
import { Context } from '../../machine/model';
import locales from './locales';

const infoSchema = z.object({
  activityDetails: z.string().min(1, { message: zodLocales.errors.required }),
});

type ApeDetailsProps = {
  company: Context['companyProfile'];
  onSave: (payload: z.infer<typeof infoSchema>) => void;
};

const ApeDetails = asBentoPage<ApeDetailsProps>(
  ({ company, Layout, onPrev, onSave }) => {
    const {
      formState: { errors, isValid },
      handleSubmit,
      register,
      watch,
    } = useForm({
      defaultValues: {
        ...company,
        activityDetails: company.activityDetails || '',
      },
      mode: 'onChange',
      resolver: zodResolver(infoSchema),
    });

    const activityDetailsValue = watch('activityDetails');
    const onSubmit = handleSubmit((values) => {
      onSave(values);
    });

    // No ideal but needed. We display this page only when the APE is included in a short list of APE codes
    // that need details and they are all in locales
    // A generic backup question is added in case there is no custom question for a code
    const activityType =
      company.activityType as keyof (typeof locales)['customQuestion'];

    return (
      <Layout data-testid="ape-details" nav={<OBNavigation onPrev={onPrev} />}>
        <Flex direction="column" height={{ base: 'full', md: 'auto' }}>
          <Header
            description={locales.formatString(locales.description, {
              activityType: company.activityType,
              formattedActivityType: formatActivityType(company.activityType),
            })}
            title={locales.title}
          />

          <Text variant="secondary">
            {locales.customQuestion[activityType] ?? locales.genericQuestion}
          </Text>

          <Flex
            as="form"
            data-testid="ape-details"
            direction="column"
            flex="1"
            height="full"
            justify="space-between"
            marginTop="space-40"
            onSubmit={onSubmit}
          >
            <Stack spacing="space-24">
              <Form.Field
                error={errors.activityDetails?.message}
                hint={`${activityDetailsValue.length}` + locales.detailsHelp}
                label={locales.label}
              >
                <Textarea
                  {...register('activityDetails')}
                  data-testid="activity-details-input"
                  maxLength={300}
                  required
                />
              </Form.Field>
            </Stack>

            <OBStickyButton isDisabled={!isValid} type="submit">
              {locales.nextButtonLabel}
            </OBStickyButton>
          </Flex>
        </Flex>
      </Layout>
    );
  },
);

export default ApeDetails;
